import React, { useState, useEffect, useCallback } from 'react';
import { Clock, BookOpen, FileText, X, RefreshCw, AlertCircle, CheckCircle, Loader2, XCircle, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QueuePanel = ({ isOpen, onClose }) => {
  const [queueData, setQueueData] = useState({ items: [], summary: { queued: 0, processing: 0, failed: 0, canceled: 0, total_pending: 0 } });
  const [loading, setLoading] = useState(true);
  const [cancelingId, setCancelingId] = useState(null);
  const [clearing, setClearing] = useState(false);

  const fetchQueue = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/ebook-queue`);
      if (response.ok) {
        const data = await response.json();
        setQueueData(data);
      }
    } catch (error) {
      console.error('Error fetching queue:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchQueue();
      // Poll every 3 seconds for real-time updates
      const interval = setInterval(fetchQueue, 3000);
      return () => clearInterval(interval);
    }
  }, [isOpen, fetchQueue]);

  const handleCancel = async (queueId) => {
    if (!window.confirm('Are you sure you want to cancel this item? The student will see "Canceled by Admin" status.')) {
      return;
    }
    
    setCancelingId(queueId);
    try {
      const response = await fetch(`${API_URL}/api/admin/ebook-queue/${queueId}/cancel`, {
        method: 'POST'
      });
      if (response.ok) {
        fetchQueue();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to cancel item');
      }
    } catch (error) {
      console.error('Error canceling item:', error);
      alert('Failed to cancel item');
    } finally {
      setCancelingId(null);
    }
  };

  const handleRetry = async (queueId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/ebook-queue/${queueId}/retry`, {
        method: 'POST'
      });
      if (response.ok) {
        fetchQueue();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to retry item');
      }
    } catch (error) {
      console.error('Error retrying item:', error);
      alert('Failed to retry item');
    }
  };

  const handleClearCompleted = async () => {
    const completedCount = queueData.items.filter(
      item => ['completed', 'failed', 'canceled'].includes(item.status)
    ).length;
    
    if (completedCount === 0) {
      alert('No completed, failed, or canceled items to clear.');
      return;
    }
    
    if (!window.confirm(`Clear ${completedCount} completed/failed/canceled item(s)? This cannot be undone.`)) {
      return;
    }
    
    setClearing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/ebook-queue/clear-completed`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        fetchQueue();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to clear queue');
      }
    } catch (error) {
      console.error('Error clearing queue:', error);
      alert('Failed to clear queue');
    } finally {
      setClearing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'queued':
        return <Badge className="bg-amber-600 text-white"><Clock className="h-3 w-3 mr-1" />Queued</Badge>;
      case 'processing':
        return <Badge className="bg-blue-600 text-white"><Loader2 className="h-3 w-3 mr-1 animate-spin" />Processing</Badge>;
      case 'completed':
        return <Badge className="bg-green-600 text-white"><CheckCircle className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'failed':
        return <Badge className="bg-red-600 text-white"><AlertCircle className="h-3 w-3 mr-1" />Failed</Badge>;
      case 'canceled':
        return <Badge className="bg-slate-600 text-white"><XCircle className="h-3 w-3 mr-1" />Canceled</Badge>;
      default:
        return <Badge className="bg-slate-500 text-white">{status}</Badge>;
    }
  };

  const getTypeIcon = (type) => {
    if (type === 'ebook') {
      return <BookOpen className="h-4 w-4 text-amber-400" />;
    } else {
      return <FileText className="h-4 w-4 text-cyan-400" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 h-full w-96 bg-slate-800 border-l border-slate-700 shadow-xl z-50 flex flex-col" data-testid="queue-panel">
      {/* Header */}
      <div className="p-4 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-purple-400" />
          <h2 className="text-lg font-semibold text-white">Send Queue</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearCompleted}
            disabled={clearing}
            className="text-slate-400 hover:text-red-400"
            title="Clear completed, failed, and canceled items"
            data-testid="clear-queue-btn"
          >
            {clearing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchQueue}
            className="text-slate-400 hover:text-white"
            data-testid="refresh-queue-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-slate-400 hover:text-white"
            data-testid="close-queue-btn"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="p-4 border-b border-slate-700 grid grid-cols-4 gap-2">
        <div className="text-center">
          <div className="text-2xl font-bold text-amber-400">{queueData.summary.queued}</div>
          <div className="text-xs text-slate-400">Queued</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-400">{queueData.summary.processing}</div>
          <div className="text-xs text-slate-400">Processing</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-400">{queueData.summary.failed}</div>
          <div className="text-xs text-slate-400">Failed</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-slate-400">{queueData.summary.canceled}</div>
          <div className="text-xs text-slate-400">Canceled</div>
        </div>
      </div>

      {/* Queue Items */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading && queueData.items.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
          </div>
        ) : queueData.items.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No items in queue</p>
          </div>
        ) : (
          queueData.items.map((item) => (
            <div
              key={item.id}
              className={`bg-slate-700/50 rounded-lg p-3 border ${
                item.status === 'processing' ? 'border-blue-500 animate-pulse' :
                item.status === 'failed' ? 'border-red-500' :
                item.status === 'canceled' ? 'border-slate-600' :
                'border-slate-600'
              }`}
              data-testid={`queue-item-${item.id}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  {getTypeIcon(item.type)}
                  <span className="text-sm font-medium text-white">
                    {item.type === 'ebook' ? 'eBook' : 'Digital Test'}
                  </span>
                </div>
                {getStatusBadge(item.status)}
              </div>

              <div className="text-sm text-slate-300 mb-1">
                {item.student_first_name} {item.student_last_name}
              </div>
              <div className="text-xs text-slate-400 mb-1">
                {item.student_email}
              </div>
              <div className="text-xs text-slate-500 mb-2">
                {item.product_name}
              </div>

              {item.error_message && (
                <div className="text-xs text-red-400 bg-red-900/30 rounded p-2 mb-2">
                  {item.error_message}
                </div>
              )}

              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>
                  {new Date(item.created_at).toLocaleTimeString()}
                </span>
                <div className="flex gap-1">
                  {item.status === 'failed' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRetry(item.id)}
                      className="h-6 px-2 text-xs text-amber-400 hover:text-amber-300"
                      data-testid={`retry-btn-${item.id}`}
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Retry
                    </Button>
                  )}
                  {(item.status === 'queued' || item.status === 'processing' || item.status === 'failed') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCancel(item.id)}
                      disabled={cancelingId === item.id}
                      className="h-6 px-2 text-xs text-red-400 hover:text-red-300"
                      data-testid={`cancel-btn-${item.id}`}
                    >
                      {cancelingId === item.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <>
                          <XCircle className="h-3 w-3 mr-1" />
                          Cancel
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-slate-700 text-center text-xs text-slate-500">
        Auto-refreshes every 3 seconds
      </div>
    </div>
  );
};

export default QueuePanel;
